import {ChangeDetectionStrategy, Component, computed, OnInit, signal} from "@angular/core"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {BrushSettingsPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/shared/panel/brush-settings-panel/brush-settings-panel.component"
import {BrushSettings} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import {ButtonComponent} from "@common/components/buttons/button/button.component"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {SliderComponent} from "@common/components/inputs/slider/slider.component"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {OperatorHighpass} from "@app/textures/texture-editor/operator-stack/operators/highpass/operator-highpass"
import {OperatorPanelBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-panel-base"
import {ActionPanelRowComponent} from "@app/common/components/menu/actions/action-panel-row/action-panel-row.component"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-highpass-panel",
    templateUrl: "./highpass-panel.component.html",
    styleUrls: ["./highpass-panel.component.scss"],
    imports: [ToggleComponent, InspectorSectionComponent, ButtonComponent, SliderComponent, ActionPanelRowComponent, BrushSettingsPanelComponent],
})
export class HighpassPanelComponent extends OperatorPanelBase<OperatorHighpass> implements OnInit {
    ngOnInit(): void {
        this.$useIndividualHV.set(!this.$operator().$isIsotropic())
    }

    readonly $showGuides = computed(() => this.$operator().$showGuides())

    protected setShowGuides(value: boolean) {
        this.$operator().setShowGuides(value)
    }

    readonly $showLowpass = computed(() => this.$operator().$showLowpass())

    protected setShowLowpass(value: boolean) {
        this.$operator().setShowLowpass(value)
    }

    readonly $showMask = computed(() => this.$operator().$showMask())

    protected setShowMask(value: boolean) {
        this.$operator().setShowMask(value)
    }

    readonly $intensity = computed(() => this.$operator().$intensity())

    protected setIntensity(value: number) {
        this.$operator().setIntensity(value)
    }

    readonly $useWrapAround = computed(() => this.$operator().$useWrapAround())

    protected setUseWrapAround(value: boolean) {
        this.$operator().setUseWrapAround(value)
    }

    readonly $correctionMode = computed(() => this.$operator().$correctionMode())

    protected setCorrectionMode(value: TextureEditNodes.OperatorHighpassCorrectionMode) {
        this.$operator().setCorrectionMode(value)
    }

    readonly $useIndividualHV = signal(false)

    setUseIndividualHV(value: boolean) {
        if (this.$useIndividualHV() === value) {
            return
        }
        this.$useIndividualHV.set(value)
        if (!value) {
            this.setSmoothingDistanceV(this.$smoothingDistanceH())
            this.setInfiniteH(false)
            this.setInfiniteV(false)
        }
    }

    readonly $infiniteH = computed(() => !this.$operator().$useH())

    protected setInfiniteH(value: boolean) {
        this.$operator().setUseH(!value)
    }

    readonly $smoothingDistanceH = computed(() => this.$operator().$smoothingDistanceH())

    protected setSmoothingDistanceH(value: number) {
        this.$operator().setSmoothingDistanceH(value)
        if (!this.$useIndividualHV()) {
            this.$operator().setSmoothingDistanceV(value)
        }
    }

    readonly $angleOffset = computed(() => this.$operator().$angleOffset())

    protected setAngleOffset(value: number) {
        this.$operator().setAngleOffset(value)
    }

    readonly $infiniteV = computed(() => !this.$operator().$useV())

    protected setInfiniteV(value: boolean) {
        this.$operator().setUseV(!value)
    }

    readonly $smoothingDistanceV = computed(() => this.$operator().$smoothingDistanceV())

    protected setSmoothingDistanceV(value: number) {
        this.$operator().setSmoothingDistanceV(value)
    }

    readonly $hasMask = computed(() => this.$operator().$hasMask())

    readonly $brushSettings = computed(() => this.$operator().$brushSettings())

    protected setBrushSettings(value: BrushSettings) {
        this.$operator().setBrushSettings(value)
    }

    addMask(): void {
        this.$operator().addMask()
    }

    removeMask(): void {
        this.$operator().removeMask()
    }
}
